<template>
  <div class="sm:col-span-3 relative">
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      Suggestie voor Collega's
    </label>
    <Listbox
      v-slot="{ open: employeeOpen }"
      :model-value="modelValue.preferredEmployees"
      @update:model-value="emit('update:field', { field: 'preferredEmployees', value: $event })"
      multiple
      :disabled="!canEdit"
    >
      <div class="relative">
        <ListboxButton
          ref="employeeListboxButtonRef"
          :class="[
            'relative w-full rounded-lg border py-2 pl-3 pr-10 text-left focus:outline-none sm:text-sm',
            !canEdit
              ? 'cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400'
              : 'cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent',
            employeeOpen && canEdit ? 'rounded-b-none border-b-gray-200 dark:border-b-gray-700' : ''
          ]"
        >
          <!-- Search input (shown when open) -->
          <input
            v-if="employeeOpen"
            ref="employeeSearchInput"
            v-model="employeeSearchQuery"
            type="text"
            class="w-full bg-transparent border-none p-0 focus:outline-none focus:ring-0 dark:text-white sm:text-sm"
            placeholder="Zoek medewerker..."
            @click.stop
            @keydown.stop
          />
          <!-- Display text (shown when closed) -->
          <template v-else>
            <span
              v-if="modelValue.preferredEmployees.length === 0"
              class="block truncate text-gray-400 dark:text-gray-500"
            >
              Selecteer medewerkers...
            </span>
            <span v-else class="block truncate dark:text-white">
              {{ selectedEmployeeNames }}
            </span>
          </template>
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400 dark:text-gray-500"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>
        <Teleport to="body">
          <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            @after-enter="focusEmployeeSearch"
            @after-leave="clearEmployeeSearch"
          >
            <ListboxOptions
              v-if="employeeOpen"
              :style="employeeDropdownStyle"
              class="z-[9999] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-base shadow-lg focus:outline-none sm:text-sm max-h-60 overflow-auto"
            >
              <div
                v-if="searchFilteredEmployees.length === 0"
                class="px-4 py-2 text-sm text-gray-500 dark:text-gray-400"
              >
                Geen medewerkers gevonden
              </div>
              <ListboxOption
                v-for="employee in searchFilteredEmployees"
                :key="employee.simplicate_id"
                :value="employee.simplicate_id"
                v-slot="{ active, selected }"
                as="template"
              >
                <li
                  :class="[
                    active
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                      : 'text-gray-900 dark:text-gray-200',
                    employeeIsPerfectMatch(employee) ? 'bg-green-50 dark:bg-green-900/30' : '',
                    'relative cursor-pointer select-none py-2 pl-10 pr-4'
                  ]"
                >
                  <div class="flex flex-col">
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                      <span
                        v-if="employeeIsPerfectMatch(employee)"
                        class="text-yellow-500 mr-1"
                        title="Perfecte match - vult beschikbaarheid precies op"
                        >&#9733;</span
                      >
                      {{ employee.name }}
                      <span class="text-gray-500 dark:text-gray-400 text-xs ml-1">
                        {{
                          [employee.function, employee.teams?.map(t => t.name).join(', ')]
                            .filter(Boolean)
                            .join(' - ')
                        }}
                      </span>
                    </span>
                    <span
                      v-if="employeeMatchPercentage(employee) !== null"
                      :class="[
                        employeeIsPerfectMatch(employee)
                          ? 'text-green-700 dark:text-green-400 font-semibold'
                          : employeeMatchPercentage(employee) >= 100
                            ? 'text-green-600 dark:text-green-400'
                            : employeeMatchPercentage(employee) >= 50
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400',
                        'text-xs font-medium'
                      ]"
                    >
                      ({{ employeeMatchPercentage(employee) }}% match)
                      <span v-if="employeeIsPerfectMatch(employee)" class="ml-1"
                        >- Perfecte fit!</span
                      >
                    </span>
                  </div>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </Teleport>
      </div>
    </Listbox>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { useElementBounding } from '@vueuse/core'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  employees: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  filteredEmployeeList: {
    type: Array,
    default: () => []
  },
  selectedEmployeeNames: {
    type: String,
    default: ''
  },
  calculateMatchPercentage: {
    type: Function,
    default: () => null
  },
  isPerfectMatch: {
    type: Function,
    default: () => false
  }
})

const emit = defineEmits(['update:field'])

// Employee search
const employeeSearchQuery = ref('')
const employeeSearchInput = ref(null)

// Dropdown button ref for Teleport positioning
const employeeListboxButtonRef = ref(null)
const employeeBounds = useElementBounding(employeeListboxButtonRef)

const employeeDropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${employeeBounds.bottom.value}px`,
  left: `${employeeBounds.left.value}px`,
  width: `${employeeBounds.width.value}px`
}))

// Focus search input when dropdown opens
const focusEmployeeSearch = () => {
  setTimeout(() => {
    employeeSearchInput.value?.focus()
  }, 0)
}

// Clear search when dropdown closes
const clearEmployeeSearch = () => {
  employeeSearchQuery.value = ''
}

// Search filtered employees (applies search on top of team/level filtering)
const searchFilteredEmployees = computed(() => {
  const query = employeeSearchQuery.value.toLowerCase().trim()
  if (!query) return props.filteredEmployeeList

  return props.filteredEmployeeList.filter(emp => {
    const name = emp.name?.toLowerCase() || ''
    const func = emp.function?.toLowerCase() || ''
    const teams = emp.teams?.map(t => t.name?.toLowerCase()).join(' ') || ''
    return name.includes(query) || func.includes(query) || teams.includes(query)
  })
})

const employeeMatchPercentage = employee => {
  return props.calculateMatchPercentage(employee, props.modelValue)
}

const employeeIsPerfectMatch = employee => {
  return props.isPerfectMatch(employee, props.modelValue)
}
</script>
